'use client';

import { useEffect, useRef, useState } from 'react';

interface MenuPosition {
  left?: string;
  right?: string;
  transform?: string;
  visibility: 'visible' | 'hidden';
}

export function useSmartMenuPosition(
  isOpen: boolean,
  menuWidth: number = 900
) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition>({
    left: '0',
    transform: 'none',
    visibility: 'hidden'
  });

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setPosition({
        left: '0',
        transform: 'none',
        visibility: 'hidden'
      });
      return;
    }

    // Calculate position after a microtask to ensure DOM is ready
    const calculatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;

      const buttonRect = button.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16; // 1rem padding from edges

      // Calculate button center
      const buttonCenter = buttonRect.left + (buttonRect.width / 2);
      
      // Calculate ideal position (centered under button)
      const idealLeft = buttonCenter - (menuWidth / 2);
      
      // Check if menu would overflow viewport
      const wouldOverflowLeft = idealLeft < padding;
      const wouldOverflowRight = idealLeft + menuWidth > viewportWidth - padding;

      let newPosition: MenuPosition = {
        visibility: 'visible'
      };

      if (wouldOverflowLeft) {
        // Align to left edge with padding
        newPosition.left = `${padding}px`;
        newPosition.transform = 'none';
      } else if (wouldOverflowRight) {
        // Align to right edge with padding
        newPosition.right = `${padding}px`;
        newPosition.left = 'auto';
        newPosition.transform = 'none';
      } else {
        // Center under button (ideal case)
        newPosition.left = '50%';
        newPosition.transform = `translateX(-50%)`;
      }

      setPosition(newPosition);
    };

    // Use requestAnimationFrame for smooth positioning
    requestAnimationFrame(calculatePosition);

    // Recalculate on resize
    const handleResize = () => {
      requestAnimationFrame(calculatePosition);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, menuWidth]);

  return { buttonRef, position };
}